/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Table } from '../../Table/Table';
import { TableBody } from '../../TableBody/TableBody';
import Hidden from '../../Hidden';
import { Grid } from '../../Grid';
import azCommonStyles from '../../../theme/globals.module.scss';
import { useLabels } from '@/hooks/useLabels';
import { trackReadMoreLinkPdp } from '@/utils/analytics/track/trackReadMoreLinkPdp';
import styles from './styles.module.scss';
import { useHeaderData } from '@/features/header/api/getHeader';
import { type ProductSkuDetails } from '@/features/product';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { type ProductModel } from '@/api/types/products-types';
import dynamic from 'next/dynamic';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { useDeviceType } from '@/utils/useDeviceType';
import { ProductSpecificationRow } from '../ProductSpecificationRow/ProductSpecificationRow';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { getFormattedDisplayNotes } from '@/utils/getFormattedDisplayNotes';
import { Accordion } from '@az/starc-ui';
import ProductDescSpecSkeletonV2 from './ProductDescSpecSkeletonV2';

export type WarrantyModalType = {
  isShown: boolean;
  showModalFrom?: string;
  warrantyType?: string;
};

const LazyWarrantyModal = dynamic(
  () => import('../ProductWarranty').then((mod) => mod.WarrantyModal),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

export type ProductDetailSpecificationProps = {
  warrantyModal?: { isShown: boolean; showModalFrom?: string; warrantyType?: string };
  setWarrantyModal?: ({ isShown }: WarrantyModalType) => void;
  closeWarrantyModal?: () => void;
  shouldShowLoadingSkeleton?: boolean;
  isSpecsAccordionOpen?: boolean;
  setIsSpecsAccordionOpen?: (isOpen: boolean) => void;
  isDescriptionAccordionOpen?: boolean;
  setIsDescriptionAccordionOpen?: (isOpen: boolean) => void;
  oemPartNumber?: string;
};

type ChildProps = {
  skuDetails: ProductSkuDetails | null | undefined;
  labels: typeof labelMap;
  isBot: boolean;
  product: ProductModel | undefined;
  hasVehicle?: boolean;
  isMobile: boolean;
  acesPDPEnabled: boolean;
} & ProductDetailSpecificationProps;

const labelMap = {
  label_ProductDisplayPage_body_PartNo: 'label_ProductDisplayPage_body_PartNo',
  lblWeight: 'label_product_display_page_body_Weight',
  lblWarranty: 'label_product_display_page_body_Warranty',
  label_CategoryPage_body_Notes: 'label_CategoryPage_body_Notes',
  hyperlink_CategoryPage_body_ReadMore: 'hyperlink_CategoryPage_body_ReadMore',
  hyperlink_CategoryPage_body_ReadLess: 'hyperlink_CategoryPage_body_ReadLess',
  label_CategoryPage_body_ProductSpecifications: 'label_CategoryPage_body_ProductSpecifications',
  label_CategoryPage_body_ProductDescription: 'label_CategoryPage_body_ProductDescription',
  label_DetailsPage_body_Location: 'label_DetailsPage_body_Location',
  label_DetailsPage_body_ApplicationSmall: 'label_DetailsPage_body_ApplicationSmall',
  lblSkuNumber: 'Label_Sku_Uppercase',
  label_product_bulletins: 'label_product_bulletins',
  label_safety_data_sheets: 'label_safety_data_sheets',
  label_More_product_specifications: 'label_More_product_specifications',
  label_cross_reference_num: 'label_cross_reference_num',
};

export const productSpecificationConstant = {
  ProductSpec: 'ProductSpec',
  readmoreLinkName: 'readmoreLinkName',
};

const ProductDetailSpecificationV2 = (props: ChildProps) => {
  const specificationCall = useRef<HTMLDivElement | null>(null);

  const showWarrantyModal = () => {
    props.setWarrantyModal?.({
      isShown: true,
      showModalFrom: productSpecificationConstant.ProductSpec,
    });
  };

  const partNumberDisplay = () => {
    const { labels } = props;

    if (props.product?.itemWeight || props.acesPDPEnabled) {
      return (
        <ProductSpecificationRow
          key={labels.label_ProductDisplayPage_body_PartNo}
          label={labels.label_ProductDisplayPage_body_PartNo}
          value={props.product?.partNumber}
          labelTestId="table-cell-part-number"
          compact
        />
      );
    }
  };

  const skuNumberDisplay = () => {
    const skuId = props.product?.itemId;
    const { labels } = props;

    if (skuId) {
      return (
        <ProductSpecificationRow
          key={labels.lblSkuNumber}
          label={`${labels.lblSkuNumber} #`}
          value={skuId}
          labelTestId="table-cell-part-number"
          compact
        />
      );
    }
  };

  const weightDisplay = () => {
    const weight = props.product?.itemWeight;
    const { labels } = props;

    if (weight) {
      return (
        <ProductSpecificationRow
          key={labels.lblWeight}
          label={labels.lblWeight}
          value={weight}
          compact
        />
      );
    }
  };

  const locationDisplay = () => {
    const pdpDetails = props.product;
    const { labels } = props;

    if (pdpDetails?.location) {
      return (
        <ProductSpecificationRow
          key={labels.label_DetailsPage_body_Location}
          label={labels.label_DetailsPage_body_Location}
          value={pdpDetails.location}
          labelTestId="product-details-location"
          valueTestId="product-details-location-value"
          compact
        />
      );
    }
  };

  const applicationDisplay = () => {
    const productDetails = props.product;
    const { labels } = props; // shortcircuit if for some reason cannot retreieve pdpInfo
    if (productDetails?.applicationNotes) {
      const applicationCount = productDetails.applicationNotes.length;
      return (
        <ProductSpecificationRow
          key={labels.label_DetailsPage_body_ApplicationSmall}
          label={labels.label_DetailsPage_body_ApplicationSmall}
          value={productDetails.applicationNotes.map((applicationItem, i) => (
            <p key={`${labels.label_DetailsPage_body_ApplicationSmall}${i}`}>
              {`${applicationCount > 1 ? i + 1 + '. - ' + applicationItem : applicationItem}`}
            </p>
          ))}
          labelTestId="details-application"
          valueTestId="details-application-value"
          compact
        />
      );
    }

    return null;
  };

  const crossReferenceDisplay = () => {
    const { labels, oemPartNumber } = props;

    if (oemPartNumber) {
      return (
        <ProductSpecificationRow
          key={labels.label_cross_reference_num}
          label={labels.label_cross_reference_num}
          value={oemPartNumber}
          labelTestId="table-cell-cross-reference"
          compact
        />
      );
    }
  };

  const renderProdSpec = () => {
    const productSpecification = (props.product?.productAttributes ?? []).filter(
      (item) => !!item.value,
    );
    const { labels } = props;
    const finalNotes = getFormattedDisplayNotes({
      ...(props.product || {}),
      showFitmentNotesOnly: false,
    });

    const warrantyDisplay =
      (props.skuDetails?.warranty ?? '') ? (
        <ProductSpecificationRow
          key={labels.lblWarranty}
          label={labels.lblWarranty}
          value={
            <button
              className={cx(azCommonStyles['az-body-1-heavy'], styles.warranty, styles.warrantyV2)}
              onClick={showWarrantyModal}
            >
              {props.skuDetails?.warranty ?? ''}
            </button>
          }
          labelTestId="table-cell-warranty"
          compact
        />
      ) : null;

    const techNotesDisplay =
      finalNotes && props.hasVehicle ? (
        <div className={styles.techNotesV2}>
          <span className={styles.notesTitle}>{labels.label_CategoryPage_body_Notes}:</span>
          <span className={styles.notesValue}>{finalNotes}</span>
        </div>
      ) : null;

    const productSpecificationDisplay = productSpecification.map((item) => {
      const productSpecList = (
        <ProductSpecificationRow
          key={`${item.name}_${item.value}_less_odd`}
          label={item.name ?? ''}
          value={item.value}
        />
      );

      return productSpecList;
    });
    let fullProductSpecificationList = productSpecificationDisplay;
    const productServiceBulletin = props.product?.itemPsbUrls ?? [];
    const msds =
      props.acesPDPEnabled && typeof props.product?.itemMsdsUrl === 'object'
        ? props.product?.itemMsdsUrl?.[0]
        : typeof props.product?.itemMsdsUrl === 'string'
          ? props.product?.itemMsdsUrl
          : null;

    if (productServiceBulletin.length > 0 || msds) {
      const psbRows = (
        <ProductSpecificationRow
          key="product_bulletins_less_odd"
          label={labels.label_product_bulletins}
          valueClassName={styles.bulletinsValue}
          value={
            <>
              {productServiceBulletin.length > 0 &&
                productServiceBulletin.map((item) => {
                  const psbUrl =
                    props.acesPDPEnabled && typeof item.url === 'object'
                      ? item.url?.[0]
                      : typeof item.url === 'string'
                        ? item.url
                        : undefined;
                  return (
                    <div key={`${item.title}`}>
                      <a
                        className={cx(
                          styles.productServiceBulletinLinks,
                          styles.productServiceBulletinLinksV2,
                          azCommonStyles['az-body-1-heavy'],
                        )}
                        href={psbUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.title}
                      </a>
                    </div>
                  );
                })}
              {msds && (
                <div>
                  <a
                    className={cx(
                      styles.productServiceBulletinLinks,
                      styles.productServiceBulletinLinksV2,
                      azCommonStyles['az-body-1-heavy'],
                    )}
                    href={msds}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {labels.label_safety_data_sheets}
                  </a>
                </div>
              )}
            </>
          }
        />
      );
      fullProductSpecificationList = productSpecificationDisplay.concat(psbRows);
    }

    return (
      <div id="product-specifications-content">
        <div className={styles.specsTableContainerV2}>
          <Table
            aria-label={labels.label_CategoryPage_body_ProductSpecifications}
            className={styles.specsTable}
          >
            <TableBody
              className={cx(styles.productSpecificationBody, styles.productSpecificationBodyV2)}
            >
              {warrantyDisplay}
              {partNumberDisplay()}
              {skuNumberDisplay()}
              {crossReferenceDisplay()}
              {weightDisplay()}
              {locationDisplay()}
              {applicationDisplay()}
              {fullProductSpecificationList}
            </TableBody>
          </Table>
          {techNotesDisplay}
        </div>
      </div>
    );
  };

  const renderProdDesc = () => {
    const desc = props.product?.marketingCopy ?? '';
    const featureAttributes = props.product?.featuresAndBenefits ?? [];
    let featureAttributesArr = null;

    if (featureAttributes?.length > 0) {
      featureAttributesArr = featureAttributes.map((featureAttribute: string) => (
        <li
          className={cx(
            azCommonStyles['az-body-1-regular'],
            styles.featurelist,
            styles.featurelistV2,
          )}
          key={`${featureAttribute}_feature_attribute`}
        >
          <div>{featureAttribute}</div>
        </li>
      ));
    }

    return (
      <div className={cx(azCommonStyles['az-body-1-regular'], styles.productDescriptionV2)}>
        {props.product?.marketingCopy && (
          <div className={styles.productDescriptionV2Desc}>
            <div
              dangerouslySetInnerHTML={(() => ({
                __html: desc,
              }))()}
            />
          </div>
        )}
        <div
          className={cx(styles.productDescriptionV2FeatureAttr, {
            [styles.productDescriptionV2FeatureAttrNoDesc]: !props.product?.marketingCopy,
          })}
        >
          <ul className={styles.feature}>{featureAttributesArr}</ul>
        </div>
      </div>
    );
  };

  const renderWarrantyModal = () => {
    const pdpDetails =
      props.product?.lineCode &&
      props.product?.partNumber &&
      // No itemWeight in ACES response
      (props.acesPDPEnabled || props.product?.itemWeight);

    const { warrantyModal, skuDetails } = props;
    let pdpDetailsComp = null;
    const warrantyType = skuDetails?.warrantyType || props.product?.warrantyType || '';
    const closeWarrantyModal = () => {
      props.setWarrantyModal?.({ isShown: false });
    };

    if (pdpDetails && warrantyModal?.isShown && warrantyType) {
      pdpDetailsComp = (
        <LazyWarrantyModal
          showWarranty={
            warrantyModal.isShown &&
            warrantyModal.showModalFrom === productSpecificationConstant.ProductSpec
          }
          closeWarrantyModal={closeWarrantyModal}
          warrantyType={warrantyType}
        />
      );
    }
    return pdpDetailsComp;
  };

  const descriptionMoreOrLess = () => {
    return (
      <>
        <div className={cx(styles.productDescriptionPart, styles.productDescriptionPartV2)}>
          <div>{renderProdDesc()}</div>
        </div>
      </>
    );
  };

  const descriptionMoreOrLessTablet = () => {
    return <div className={cx(azCommonStyles['az-body-1-regular'])}>{renderProdDesc()}</div>;
  };

  const renderSpecifications = () => {
    const { labels, isDescriptionAccordionOpen, setIsDescriptionAccordionOpen } = props;
    const hasDescription =
      !!props.product?.marketingCopy && !!props.product?.featuresAndBenefits?.length;

    if (!hasDescription) {
      return null;
    }

    return (
      <Accordion
        className={cx(styles.accordionProductDescriptionV2)}
        name="product-description"
        open={isDescriptionAccordionOpen}
        onToggle={(isOpen) => {
          setIsDescriptionAccordionOpen?.(isOpen);
          isOpen && trackReadMoreLinkPdp('productDescription');
        }}
        headerOptions={{
          headerElement: (
            <span
              className={cx(styles.accordionProductDescriptionHeaderV2)}
              data-testid="product-description-heading"
            >
              {labels.label_CategoryPage_body_ProductDescription.toLowerCase()}
            </span>
          ),
          triggerClassName: styles.accordionProductDescriptionHeader,
        }}
      >
        {isDescriptionAccordionOpen && (
          <div className={styles.productSpecificationPart}>
            <Hidden only={['sm', 'md']} implementation="js">
              {descriptionMoreOrLess()}
            </Hidden>
            <Hidden only={['xl', 'lg']} implementation="js">
              {descriptionMoreOrLessTablet()}
            </Hidden>
          </div>
        )}
      </Accordion>
    );
  };

  const { labels, isSpecsAccordionOpen, setIsSpecsAccordionOpen } = props;
  return (
    <div>
      {renderWarrantyModal()}
      <div
        className={cx(styles.productSpecificationWrapper, styles.productSpecificationWrapperV2)}
        ref={specificationCall}
        data-scroll-target="product-specifications"
      >
        <Grid container spacing={0} className={styles.gridContainerV2}>
          <Grid
            item
            xs={12}
            md={12}
            lg={12}
            xl={12}
            className={props.shouldShowLoadingSkeleton ? styles.contentDivider : ''}
          >
            {props.shouldShowLoadingSkeleton ? (
              <ProductDescSpecSkeletonV2 />
            ) : (
              <div
                className={styles.productSpecificationPart}
                id="product-specifications-container"
              >
                <Accordion
                  className={cx(styles.accordionProductDescriptionV2)}
                  name="product-specifications"
                  open={isSpecsAccordionOpen}
                  onToggle={(isOpen) => {
                    setIsSpecsAccordionOpen?.(isOpen);
                    isOpen && trackReadMoreLinkPdp('productSpecifications');
                  }}
                  headerOptions={{
                    headerElement: (
                      <span
                        className={cx(styles.accordionProductDescriptionHeaderV2)}
                        data-testid="product-Specification-heading"
                      >
                        {labels.label_CategoryPage_body_ProductSpecifications.toLowerCase()}
                      </span>
                    ),
                    triggerClassName: styles.accordionProductDescriptionHeader,
                  }}
                >
                  {isSpecsAccordionOpen && renderProdSpec()}
                </Accordion>
              </div>
            )}
          </Grid>
          <Grid item xs={12} md={12} lg={12} xl={12}>
            {props.shouldShowLoadingSkeleton ? (
              <ProductDescSpecSkeletonV2 />
            ) : (
              renderSpecifications()
            )}
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

const WrappedProductDetailSpecificationV2 = (props: ProductDetailSpecificationProps) => {
  const { data: productDetails, status: productsStatus } = useProductDetails();
  const skuDetailsResult = useProductSkuDetails({
    skuIds:
      productsStatus === 'success' && productDetails.product?.itemId
        ? [productDetails.product.itemId]
        : null,
  });
  const skuDetails = skuDetailsResult.isSuccess ? skuDetailsResult.data[0] : null;
  const labels = useLabels(labelMap);
  const { data: headerData } = useHeaderData();
  const hasVehicle = headerData?.vehicleMap?.vehicleId !== '';
  const isBot = useDeviceType() === 'bot';
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { acesEnabled } = useAcesFlags();
  const [isSpecsAccordionOpen, setIsSpecsAccordionOpen] = useState(isBot);
  const [isDescriptionAccordionOpen, setIsDescriptionAccordionOpen] = useState(isBot);

  useEffect(() => {
    const openSpecsPanel = () => {
      setIsSpecsAccordionOpen(true);
    };
    window.addEventListener('product-specifications-expansion', openSpecsPanel);
    return () => {
      window.removeEventListener('product-specifications-expansion', openSpecsPanel);
    };
  }, []);

  return (
    <ProductDetailSpecificationV2
      {...props}
      isBot={isBot}
      labels={labels}
      hasVehicle={hasVehicle}
      product={productDetails?.product}
      skuDetails={skuDetails}
      isMobile={isMobile}
      acesPDPEnabled={acesEnabled}
      isSpecsAccordionOpen={isSpecsAccordionOpen}
      setIsSpecsAccordionOpen={setIsSpecsAccordionOpen}
      isDescriptionAccordionOpen={isDescriptionAccordionOpen}
      setIsDescriptionAccordionOpen={setIsDescriptionAccordionOpen}
    />
  );
};

export default WrappedProductDetailSpecificationV2;
